import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { useAuth, isAdmin } from "../lib/auth";
import { initials } from "../lib/helpers";
import { ROLE_LABELS } from "../lib/types";
import { ChatWidget } from "./ai/ChatWidget";

interface NavItem {
  to: string;
  icon: string;
  label: string;
  badge?: number;
}

const STUDENT_NAV: NavItem[] = [
  { to: "/student/dashboard", icon: "fa-grip", label: "Tabloul meu" },
  { to: "/student/cursuri", icon: "fa-book", label: "Cursurile mele" },
  { to: "/student/catalog", icon: "fa-compass", label: "Catalog cursuri" },
  { to: "/student/certificate", icon: "fa-certificate", label: "Certificate" },
  { to: "/student/asistent", icon: "fa-robot", label: "Asistent" },
];

const ADMIN_NAV: NavItem[] = [
  { to: "/admin/dashboard", icon: "fa-grip", label: "Tablou de bord" },
  { to: "/admin/studenti", icon: "fa-user-graduate", label: "Studenți" },
  { to: "/admin/cursuri", icon: "fa-book", label: "Cursuri" },
  { to: "/admin/asistent", icon: "fa-robot", label: "Asistent" },
];

const ADMIN_CONFIG: NavItem[] = [
  { to: "/admin/administratori", icon: "fa-user-shield", label: "Administratori" },
];

function useAIEnabled(): boolean {
  const [enabled, setEnabled] = useState(false);
  useEffect(() => {
    let alive = true;
    fetch("/api/health")
      .then((r) => r.json())
      .then((j) => alive && setEnabled(Boolean(j.ai_assistant_enabled)))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);
  return enabled;
}

export function AppShell({ children }: { children?: ReactNode }) {
  const { user, logout } = useAuth();
  const aiEnabled = useAIEnabled();
  if (!user) return null;
  const admin = isAdmin(user);
  const baseNav = admin ? ADMIN_NAV : STUDENT_NAV;
  const navItems = aiEnabled ? baseNav : baseNav.filter((n) => !n.to.endsWith("/asistent"));
  const userInitials = initials(user.full_name);

  return (
    <div className="app">
      <aside className="sidebar">
        <div className="sidebar-brand">
          <Link to="/" className="brand">
            <img src="/logo.svg" alt="" width={38} height={38} />
            <div className="brand-text">
              <span>Marie Curie</span>
              <small style={admin ? { color: "var(--c-accent)" } : undefined}>
                {admin ? "Administrator" : "Portal cursant"}
              </small>
            </div>
          </Link>
        </div>
        <nav className="sidebar-nav">
          {navItems.map((it) => (
            <SidebarItem key={it.to} {...it} />
          ))}
          {admin && (
            <>
              <div className="sidebar-section">Configurare</div>
              {ADMIN_CONFIG.map((it) => (
                <SidebarItem key={it.to} {...it} />
              ))}
            </>
          )}
          <a
            href="#"
            className="sidebar-item"
            onClick={(e) => {
              e.preventDefault();
              logout();
            }}
          >
            <i className="fa-solid fa-right-from-bracket ico"></i>Ieșire
          </a>
        </nav>
        <div className="sidebar-user">
          <div
            className="avatar"
            style={admin ? { background: "linear-gradient(135deg, var(--c-accent), #8a0a26)" } : undefined}
          >
            {userInitials}
          </div>
          <div className="meta">
            <strong>{user.full_name}</strong>
            <small>{user.specialty || ROLE_LABELS[user.role]}</small>
          </div>
        </div>
      </aside>
      <div className="main">{children ?? <Outlet />}</div>
      <ChatWidget enabled={aiEnabled} />
    </div>
  );
}

function SidebarItem({ to, icon, label, badge }: NavItem) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) => "sidebar-item" + (isActive ? " active" : "")}
    >
      <i className={`fa-solid ${icon} ico`}></i>
      {label}
      {badge != null && <span className="badge">{badge}</span>}
    </NavLink>
  );
}

interface AppBarProps {
  crumbs?: { label: string; to?: string }[];
  rightExtras?: ReactNode;
}

export function AppBar({ crumbs, rightExtras }: AppBarProps) {
  const { user } = useAuth();
  const admin = isAdmin(user);
  const userInitials = user ? initials(user.full_name) : "?";
  return (
    <header className="appbar">
      <div className="crumbs">
        {crumbs?.map((c, i) => {
          const last = i === crumbs.length - 1;
          return (
            <span key={i}>
              {i > 0 && <span className="sep">/</span>}
              {c.to && !last ? <Link to={c.to}>{c.label}</Link> : <span className={last ? "current" : ""}>{c.label}</span>}
            </span>
          );
        })}
      </div>
      <div className="appbar-actions">
        {rightExtras}
        <div
          className="avatar"
          style={admin ? { background: "linear-gradient(135deg, var(--c-accent), #8a0a26)" } : undefined}
        >
          {userInitials}
        </div>
      </div>
    </header>
  );
}

export function useCurrentPath() {
  return useLocation().pathname;
}
